import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers';
import { ensureCommunityChannels, replaceCommunityChannels } from '../api/channels';
import type { Channel } from '../community-view/channels';

export const channelKeys = {
  list: (communityId: string) => ['channels', communityId] as const,
};

/** Legacy per-device channel store — migrated to the table on the owner's next visit. */
function takeLegacyLocalChannels(communityId: string): Channel[] | null {
  try {
    const key = `winr-channels:${communityId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Channel[];
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
}

function clearLegacyLocalChannels(communityId: string): void {
  try {
    localStorage.removeItem(`winr-channels:${communityId}`);
    localStorage.removeItem(`winr-channels:${communityId}:version`);
  } catch {
    /* best-effort */
  }
}

/**
 * The community's channel rail, server-backed. When the owner still has a
 * legacy localStorage copy (the pre-table store, likely their most-authored
 * version), it's migrated into the table once, then the key is retired.
 */
export function useChannels(communityId: string, ownerId?: string | null) {
  const { user } = useAuth();
  const isOwner = Boolean(user && ownerId && user.id === ownerId);

  return useQuery({
    queryKey: channelKeys.list(communityId),
    enabled: Boolean(communityId),
    queryFn: async () => {
      const server = await ensureCommunityChannels(communityId);
      if (isOwner) {
        const legacy = takeLegacyLocalChannels(communityId);
        if (legacy) {
          try {
            const replaced = await replaceCommunityChannels(communityId, legacy);
            clearLegacyLocalChannels(communityId);
            return replaced;
          } catch {
            /* keep server rows; retry next load */
          }
        }
      }
      return server;
    },
  });
}

export function useReplaceChannels(communityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channels: Channel[]) => replaceCommunityChannels(communityId, channels),
    onSuccess: (channels) => {
      qc.setQueryData(channelKeys.list(communityId), channels);
    },
  });
}
